import { colors } from '@chegg-ui/theme'
import {
  BLURRED_CARD_KEY,
  CARD_STATES,
  GRAMMAR,
  PLAGIARISM,
} from '@src/components/Cards/constants'
import { CardContent, Comment } from '@src/components/Editor/types'
import {
  EXPERT_CHECK_DISPLAY,
  GRAMMAR_DISPLAY,
  PLAGIARISM_DISPLAY,
} from '@src/layouts/shared/NavTabs/types'
import { getCheckCategoryPath } from '@src/layouts/shared/NavTabs/utils'
import { getPaidMember } from '@src/state/auth/selectors'
import { isDemoSelector } from '@src/state/router/selectors'
import { RootState } from '@src/state/types'
import { getWFELS } from '@src/utils/localStorage'
import { createSelector } from 'reselect'
import { CheckTabsVisited } from './reducer'

import {
  getUpsellVariation,
  selectDisablePartialPlagiarismCheck,
} from '@src/state/featureFlags/selectors'
import { getSiteTermsLink } from '@src/state/site/selectors'
import {
  getCategoryFromType,
  getMaxFreeComments,
  selectAllComments,
  selectCommentsWithCustomEntities,
  selectEntitledComments,
  selectGatedComments,
  selectGrammarCommentsCount,
  selectInteractableComments,
  selectInteractableGrammarComments,
  selectInteractablePlagComments,
  selectPlagiarismSimilarityScore,
  selectPlagirismCommentsCount,
} from '../document/selectors'
import { CheckCategory, EXPERT_CHECK, TypePriorities } from '../document/types'
import { getSite, selectPlusSiteName } from '../site/selectors'
import {
  FreemiumPlagiarismCheckModalCSPOverrides,
  UPSELL_CONTROL_NO_PLAG,
  UPSELL_CONTROL_PLAG_FOUND,
} from './constants'

export type CheckCategoryPaths = { [K in CheckCategory]: string }

export const getIsWorkspaceActive = (state: RootState) => !!state.workspace.id
export const getFilters = (state: RootState) => state.workspace.filters
export const selectedEntityId = (state: RootState) => state.workspace.selectedEntityId
export const getFilename = (state: RootState) => state.workspace.filename
export const getUploadId = (state: RootState) => state.workspace.id

export const selectCommentCount = createSelector<RootState, Comment[], number>(
  [state => selectEntitledComments(state)],
  comments => comments.length
)
export const selectLastCommentStatus = createSelector(
  [(state: RootState) => selectCommentsWithCustomEntities(state)],
  comments =>
    comments &&
    comments.length &&
    (comments[comments.length - 1] as CardContent).cardState
)
export const selectCurrentCheckCategory = (state: RootState): CheckCategory =>
  state.workspace.currentCheckCategory

export const selectInitialCheckCategory = (state: RootState): CheckCategory | null =>
  state.workspace.initialCheckCategory

export const selectCheckTabsVisited = (state: RootState): CheckTabsVisited =>
  state.workspace.checkTabsVisited

export const selectShowPlagiarismGate = createSelector(
  [getPaidMember, isDemoSelector, selectCurrentCheckCategory],
  (paidMember, isDemo, category): boolean =>
    !paidMember && !isDemo && category === PLAGIARISM
)

export interface NavFilter {
  category: CheckCategory
  displayText: string
}

export interface TabData extends NavFilter {
  count?: number
  similarityScore?: number
}

export interface CheckCompletionStatus {
  [type: string]: boolean
}

export const tabs: NavFilter[] = [
  {
    category: GRAMMAR,
    displayText: GRAMMAR_DISPLAY,
  },
  {
    category: PLAGIARISM,
    displayText: PLAGIARISM_DISPLAY,
  },
  {
    category: EXPERT_CHECK,
    displayText: EXPERT_CHECK_DISPLAY,
  },
]

export const getCommentsByCategory = (category: CheckCategory, comments: CardContent[]) =>
  comments.filter(comment => getCategoryFromType(comment.type) === category)

export const getTabData = createSelector<
  RootState,
  number | undefined,
  CardContent[],
  TabData[]
>(
  [
    state => selectPlagiarismSimilarityScore(state),
    state => selectInteractableComments(state),
  ],
  (similarityScore, comments): TabData[] =>
    tabs.map(({ category, displayText }) => {
      if (category === GRAMMAR) {
        return {
          category,
          displayText,
          count: getCommentsByCategory(category, comments).length,
        }
      } else if (category === PLAGIARISM) {
        return { category, displayText, similarityScore }
      }
      return { category, displayText }
    })
)

export const selectCategoryCommentCount = createSelector<
  RootState,
  CheckCategory,
  number,
  number,
  number | undefined
>(
  [
    selectCurrentCheckCategory,
    state => selectGrammarCommentsCount(state),
    state => selectPlagirismCommentsCount(state),
  ],
  (currentCategory, grammarCount, plagiarismCount) => {
    if (currentCategory === GRAMMAR) {
      return grammarCount
    } else if (currentCategory === PLAGIARISM) {
      return plagiarismCount
    }
    return
  }
)

export const selectRemainingCount = createSelector(
  [selectCategoryCommentCount, (state: RootState) => getMaxFreeComments(state)],
  (totalCount, maxFreeComments) => {
    return (totalCount || 0) - maxFreeComments
  }
)

export const hasCheckedPlag = (state: RootState): boolean => {
  if (typeof state.document.plagiarismScore === 'number') {
    return true
  }
  if (typeof getWFELS()?.plagiarismScore === 'number') {
    return true
  }
  return false
}

export const selectAllCommentsInteracted = createSelector<
  RootState,
  boolean,
  CheckCategory,
  CardContent[],
  CardContent[],
  CardContent[],
  boolean
>(
  [
    hasCheckedPlag,
    selectCurrentCheckCategory,
    state => selectInteractablePlagComments(state),
    state => selectInteractableGrammarComments(state),
    state => selectAllComments(state),
  ],
  (
    plagiarismCommentsReceived,
    category,
    interactablePlagComments,
    interactableGrammarComments,
    allComments
  ): boolean => {
    if (category === GRAMMAR) {
      const grammarComments = getCommentsByCategory(GRAMMAR, allComments)
      const hasGrammar = grammarComments.length > 0
      const hasInteractableGrammar = interactableGrammarComments.length > 0
      return !hasInteractableGrammar && hasGrammar
    }
    const plagComments = getCommentsByCategory(PLAGIARISM, allComments)
    const hasPlag = plagComments.length > 0
    const hasInteractablePlag = interactablePlagComments.length > 0
    return (hasPlag && !hasInteractablePlag) || plagiarismCommentsReceived
  }
)

export const selectCardsAreComplete = createSelector(
  [
    selectCommentCount,
    selectLastCommentStatus,
    selectCurrentCheckCategory,
    (state: RootState) => selectGatedComments(state),
  ],
  (commentCount, status, category, gatedComments) => {
    return (
      commentCount === 0 &&
      status !== CARD_STATES.NEW &&
      status !== CARD_STATES.READ &&
      category !== EXPERT_CHECK &&
      gatedComments.length === 0
    )
  }
)

export const selectShouldScrollBlurredCard = createSelector(
  [selectedEntityId],
  entityId => entityId === BLURRED_CARD_KEY
)

export const selectUpdateHaxMaxFreeComments = (commentCount: number) =>
  createSelector([getMaxFreeComments], maxFreeComments => commentCount >= maxFreeComments)

export const selectCheckErrors = createSelector(
  [
    selectCurrentCheckCategory,
    (state: RootState) => state.checks,
    (state: RootState) => state.workspace.grammarAnalyzeFailed,
    (state: RootState) => state.workspace.plagiarismAnalyzeFailed,
  ],
  (category, checks, grammarAnalyzeFailed, plagiarismAnalyzeFailed): boolean => {
    if (category === PLAGIARISM) {
      return (
        plagiarismAnalyzeFailed ||
        (checks.plagiarism && checks.plagiarism.status === 'FAILED')
      )
    }
    return (
      grammarAnalyzeFailed ||
      Object.keys(checks)
        .filter(type => type !== 'plagiarism')
        .some(type => checks[type].status === 'FAILED')
    )
  }
)

export const selectReadyToCheckPlagiarism = createSelector(
  [
    selectCurrentCheckCategory,
    (state: RootState) => state.checks,
    (state: RootState) => state.workspace.checksRunning,
  ],
  (category, checks, checksRunning): boolean =>
    category === PLAGIARISM && !checksRunning && !checks.plagiarism
)

export const selectShowSimilarityScoreForCards = createSelector<
  RootState,
  CheckCategory,
  number | undefined,
  boolean
>(
  [
    state => selectCurrentCheckCategory(state),
    state => selectPlagiarismSimilarityScore(state),
  ],
  (category, similarityScore) => category === PLAGIARISM && similarityScore !== undefined
)

export const selectPathsForCheckCategories = (state: RootState): CheckCategoryPaths => {
  const { id } = state.workspace
  const isDemo = isDemoSelector(state)

  return {
    grammar: getCheckCategoryPath({
      paperId: id,
      checkCategory: GRAMMAR,
      isDemo,
    }),
    plagiarism: getCheckCategoryPath({
      paperId: id,
      checkCategory: PLAGIARISM,
      isDemo,
    }),
    'expert-check': '',
  }
}

export const selectIsNewUpload = (state: RootState): boolean | undefined =>
  state.workspace.newUpload
export function selectIsInPaymentFlow(state: RootState) {
  return !!state.workspace.inPaymentFlow
}

export const selectPartialPlagiarismFound = (state: RootState) =>
  state.workspace.partialPlagiarismFound

export const FREEMIUM_PLAGFOUND_HEADER = 'We found plagiarism errors in your paper'
export const FREEMIUM_NOPLAG_HEADER = 'Run a complete plagiarism check'
export const FREEMIUM_PARTIAL_PLAG_DISABLED_HEADER = 'Check complete!'

export const selectPaperUploadedUnstyledHeaderText = createSelector(
  [selectPartialPlagiarismFound, selectDisablePartialPlagiarismCheck],
  (plagFound, disablePartialPlagiarismCheck) => {
    if (disablePartialPlagiarismCheck) {
      return FREEMIUM_PARTIAL_PLAG_DISABLED_HEADER
    }
    return plagFound ? FREEMIUM_PLAGFOUND_HEADER : FREEMIUM_NOPLAG_HEADER
  }
)

export const selectCspOverrides = createSelector(
  [getUpsellVariation],
  variation => FreemiumPlagiarismCheckModalCSPOverrides[variation]
)

export const selectPaperUploadedUnstyledUpsellCopy = createSelector(
  [selectPartialPlagiarismFound, getSite, selectPlusSiteName, selectCspOverrides],
  (plagFound, site, siteNamePlus, override): string => {
    if (override) {
      return plagFound ? override.plagiarismFoundCopy : override.plagiarismNotFoundCopy
    }

    return plagFound ? UPSELL_CONTROL_PLAG_FOUND : UPSELL_CONTROL_NO_PLAG
  }
)

export const selectShowTermsLink = createSelector(
  [selectCspOverrides, getSiteTermsLink],
  (override, termsLink): string | null => {
    if (override?.showTermsAndConditions === false) {
      return null
    }
    return termsLink
  }
)

export const selectPaperUploadedUnstyledUpsellCopyNoFreeTrial = createSelector<
  RootState,
  string,
  string
>([state => selectPlusSiteName(state)], siteNamePlus => {
  return `Sign up for ${siteNamePlus} to see your plagiarism results. Cancel anytime.`
})

export const FREEMIUM_DISMISS_CTA = 'No thanks, show grammar results only.'
export const selectPaperUploadedUnstyledDismissCopy = (state: RootState) =>
  FREEMIUM_DISMISS_CTA

export const SEE_MY_RESULTS = 'See my results'
export const START_YOUR_TRIAL = 'Start your trial*'
export const START_YOUR_SUBSCRIPTION = 'Start your subscription'
export const GET_STARTED = 'Get started'
export const START_A_FREE_TRIAL = 'Start a free trial*'
export const START_A_SUBSCRIPTION = 'Start a subscription'
export const START_FREE_TRIAL = 'Start free trial*'
export const START_SUBSCRIPTION = 'Start subscription'
export const CREATE_ACCOUNT = 'Create Account'

export const selectPlagLockText = createSelector<
  RootState,
  boolean,
  string | null,
  string
>(
  [state => selectPartialPlagiarismFound(state), selectShowTermsLink],
  (plagFound, cspUpsellHasFreeTrial) => {
    const copy = !cspUpsellHasFreeTrial ? START_A_SUBSCRIPTION : START_A_FREE_TRIAL

    return plagFound
      ? `We found plagiarism errors in your paper. ${copy} to see your plagiarism results.`
      : `Run a complete plagiarism check. ${copy} to see all writing suggestions.`
  }
)

export const selectPlagLockTextNoFreeTrial = createSelector<
  RootState,
  boolean,
  string,
  string
>(
  [state => selectPartialPlagiarismFound(state), state => selectPlusSiteName(state)],
  (plagFound, plusSiteName) =>
    plagFound
      ? `We found plagiarism errors in your paper. Sign up for ${plusSiteName} to see your plagiarism results.`
      : `Run a complete plagiarism check. Sign up for ${plusSiteName} to see all writing suggestions.`
)

export const PLAGFOUND = 'Plagiarism found'
export const NOPLAGFOUND = 'Check for plagiarism'
export const selectV2PlagiarismText = createSelector(
  [selectPartialPlagiarismFound],
  plagFound => (plagFound ? PLAGFOUND : NOPLAGFOUND)
)

export const selectV2PlagiarismBgColor = createSelector(
  [selectPartialPlagiarismFound],
  plagFound => (plagFound ? colors.coralXlight : colors.mustardXlight)
)

export const selectV2PlagiarismFill = createSelector(
  [selectPartialPlagiarismFound],
  plagFound => (plagFound ? colors.coral : colors.mustard)
)

export const CHECKER_PRIORITIES: TypePriorities = {
  language: 1,
  wtai: 2,
  rules: 2,
  classifiers: 2,
  spellcheck: 3,
}



// WEBPACK FOOTER //
// ./src/state/workspace/selectors.ts